import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import {
  AddBoxOutlined,
  HighlightOffRounded,
  IndeterminateCheckBoxOutlined,
  Search,
} from '@material-ui/icons';
import { TreeItem, TreeView } from '@material-ui/lab';
import clsx from 'clsx';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentTemplateType, ICompany } from '../../api-client/autogenerated';
import { getCompaniesState } from '../../features/companies/selector';
import { getProjectUsersState } from '../../features/project/selectors';
import { addSnackbar } from '../../features/snackbar/actions';
import { getUserPermissionForCurrentDocType, hasAdminPermissions } from '../../scripts/store-utils';
import { getDisplayDivision } from '../document-display/DocumentDisplay';
import CompanyUserTreeView from '../form-pages/user-groups/CompanyUserTreeView';
import { CancelButton, SubmitButton } from '../custom-components/CustomButtons';
import LabelWithCheckbox from '../custom-components/LabelWithCheckbox';
import CircularLoader from '../loader/CircularLoader';
import {
  AssociationDocument,
  AssociationDocumentType,
  filterCompaniesBySearch,
  filterGroupsBySearch,
  groupDocumentsForAssociation,
  hasAtLeastViewPermission,
} from './associateUsersGrouping';

const useStyles = makeStyles((theme) => ({
  title: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '26px',
    lineHeight: '30px',
    textAlign: 'left',
    color: '#0947B9',
    textTransform: 'none',
  },
  rootIconButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  columns: {
    display: 'flex',
    gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  column: {
    flex: 1,
    minWidth: 0,
  },
  columnHeading: {
    fontWeight: 500,
    fontSize: 18,
    lineHeight: '21px',
  },
  scrollArea: {
    maxHeight: '55vh',
    overflowY: 'auto',
  },
  treeRoot: {
    marginTop: 16,
    padding: 6,
    border: '1px solid #949494',
    borderRadius: '4px',
  },
  treeLabel: {
    padding: 0,
    fontSize: '16px',
    lineHeight: '24px',
  },
  treeLabelParent: {
    fontWeight: 500,
  },
  treeLabelChild: {
    fontWeight: 400,
  },
  treeGroup: {
    marginLeft: '22px',
  },
  // A node's expand icon sits on the first line of its label, not centered against every line when
  // the label wraps. The icon is 18px and the label's lines are 24px.
  treeContent: {
    alignItems: 'flex-start',
  },
  treeIconContainer: {
    marginTop: 3,
  },
}));

type Props = {
  open: boolean;
  handleClose: () => void;
  type: AssociationDocumentType;
  /** Only the documents on the page the dialog was opened from. */
  documents: AssociationDocument[];
  /** Adds the users to the notification list of each document. Rejects on failure. */
  onAssociate: (userIds: string[], documentIds: string[]) => Promise<void>;
};

function SearchField(props: { label: string; value: string; onChange: (value: string) => void }) {
  const { label, value, onChange } = props;
  return (
    <TextField
      fullWidth
      size="small"
      variant="outlined"
      placeholder={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ 'aria-label': label }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search style={{ color: '#B2B1B2' }} />
          </InputAdornment>
        ),
      }}
      style={{ marginTop: 8 }}
    />
  );
}

export default function AssociateUsersWithDocumentsDialog(props: Props) {
  const { open, handleClose, type, documents, onAssociate } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const companies = useSelector(getCompaniesState);
  const projectUsers = useSelector(getProjectUsersState);

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [documentSearch, setDocumentSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDrawings = type === AssociationDocumentType.Drawings;
  const documentNoun = isDrawings ? 'Drawings' : 'Specifications';

  useEffect(() => {
    if (open) return;
    setSelectedUserIds([]);
    setSelectedDocumentIds([]);
    setUserSearch('');
    setDocumentSearch('');
  }, [open]);

  // Only project users who can at least view this kind of document are offered, so everyone listed
  // already has access to every document. Recomputed on open: the document templates the
  // permission check reads load asynchronously.
  const eligibleCompanies = useMemo<ICompany[]>(() => {
    const docType = isDrawings
      ? DocumentTemplateType.Drawings
      : DocumentTemplateType.Specifications;
    const eligibleUserIds = new Set(
      projectUsers
        .filter((projectUser) =>
          hasAtLeastViewPermission(
            getUserPermissionForCurrentDocType(projectUser, docType),
            hasAdminPermissions(projectUser.user),
          ),
        )
        .map(({ userId }) => userId),
    );
    return companies
      .map((company) => ({
        ...company,
        users: company.users?.filter((user) => eligibleUserIds.has(user.id)),
      }))
      .filter(({ users }) => users && users.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, projectUsers, isDrawings, open]);

  const visibleCompanies = useMemo(() => filterCompaniesBySearch(eligibleCompanies, userSearch), [
    eligibleCompanies,
    userSearch,
  ]);

  const allGroups = useMemo(() => groupDocumentsForAssociation(type, documents), [type, documents]);

  const getGroupLabel = (key: string) => {
    if (isDrawings) return key || 'No Prefix';
    return key ? getDisplayDivision(key) : 'Other';
  };

  const groups = useMemo(
    () => filterGroupsBySearch(allGroups, documentSearch, getGroupLabel),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allGroups, documentSearch, isDrawings],
  );

  const handleCheckCompany = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
    companyId: string,
  ) => {
    const companyUserIds =
      visibleCompanies.find(({ id }) => id === companyId)?.users?.map(({ id }) => id) || [];
    setSelectedUserIds((prev) =>
      checked ? _.uniq([...prev, ...companyUserIds]) : _.difference(prev, companyUserIds),
    );
  };

  const handleCheckUser = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
    userId: string,
  ) =>
    setSelectedUserIds((prev) => (checked ? _.uniq([...prev, userId]) : _.without(prev, userId)));

  const handleCheckGroup = (checked: boolean, documentIds: string[]) =>
    setSelectedDocumentIds((prev) =>
      checked ? _.uniq([...prev, ...documentIds]) : _.difference(prev, documentIds),
    );

  const handleCheckDocument = (checked: boolean, documentId: string) =>
    setSelectedDocumentIds((prev) =>
      checked ? _.uniq([...prev, documentId]) : _.without(prev, documentId),
    );

  const handleAssociate = async () => {
    setIsSubmitting(true);
    try {
      await onAssociate(selectedUserIds, selectedDocumentIds);
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: `Successfully associated ${selectedUserIds.length} ${
            selectedUserIds.length === 1 ? 'user' : 'users'
          } with ${selectedDocumentIds.length} ${documentNoun.toLowerCase()}.`,
          severity: 'success',
        }),
      );
    } catch (e) {
      // The API client reports the error to the user; the dialog stays open so nothing is lost.
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.title}>
        Associate Users with {documentNoun}
        <IconButton
          style={{ right: '18px', top: '14px', position: 'absolute' }}
          onClick={handleClose}
          classes={{ root: classes.rootIconButton }}
        >
          <HighlightOffRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography style={{ marginBottom: 16 }}>
          Everyone on this list already has at least VIEW access to all of these {documentNoun}.
          Associated users get notifications from CENTERLINE when their documents get tagged or
          updated.
        </Typography>
        <div className={classes.columns}>
          <div className={classes.column}>
            <Typography className={classes.columnHeading}>
              Users ({selectedUserIds.length} selected)
            </Typography>
            <SearchField label="Search users" value={userSearch} onChange={setUserSearch} />
            <div className={classes.scrollArea}>
              {visibleCompanies.length === 0 && (
                <Typography style={{ marginTop: 16 }}>No matching users.</Typography>
              )}
              <CompanyUserTreeView
                // Remounted when the search changes, so matches start expanded.
                key={userSearch.trim()}
                defaultExpanded={userSearch.trim() ? visibleCompanies.map(({ id }) => id) : []}
                companies={visibleCompanies}
                selectedUsers={selectedUserIds}
                handleCheckCompany={handleCheckCompany}
                handleCheckNestedUser={handleCheckUser}
              />
            </div>
          </div>
          <div className={classes.column}>
            <Typography className={classes.columnHeading}>
              {documentNoun} ({selectedDocumentIds.length} selected)
            </Typography>
            <SearchField
              label={`Search ${documentNoun.toLowerCase()}`}
              value={documentSearch}
              onChange={setDocumentSearch}
            />
            <div className={classes.scrollArea}>
              {groups.length === 0 && (
                <Typography style={{ marginTop: 16 }}>
                  No matching {documentNoun.toLowerCase()}.
                </Typography>
              )}
              <TreeView
                // Remounted when the search changes, so matches start expanded.
                key={documentSearch.trim()}
                defaultExpanded={
                  documentSearch.trim() ? groups.map(({ key }) => `group-${key || 'none'}`) : []
                }
                multiSelect
                defaultCollapseIcon={<IndeterminateCheckBoxOutlined />}
                defaultExpandIcon={<AddBoxOutlined />}
                className={classes.treeRoot}
              >
                {groups.map((group) => {
                  const groupDocumentIds = group.documents.map(({ id }) => id);
                  const numSelected = groupDocumentIds.filter((id) =>
                    selectedDocumentIds.includes(id),
                  ).length;
                  return (
                    <TreeItem
                      key={group.key || 'none'}
                      nodeId={`group-${group.key || 'none'}`}
                      label={
                        <LabelWithCheckbox
                          alignTop
                          label={getGroupLabel(group.key)}
                          indeterminate={numSelected > 0 && numSelected < groupDocumentIds.length}
                          checked={numSelected === groupDocumentIds.length}
                          onChange={(e, checked) => handleCheckGroup(checked, groupDocumentIds)}
                        />
                      }
                      classes={{
                        label: clsx(classes.treeLabel, classes.treeLabelParent),
                        group: classes.treeGroup,
                        content: classes.treeContent,
                        iconContainer: classes.treeIconContainer,
                      }}
                    >
                      {group.documents.map((document) => (
                        <TreeItem
                          key={document.id}
                          nodeId={document.id}
                          label={
                            <LabelWithCheckbox
                              alignTop
                              label={[document.number, document.title]
                                .filter((s) => s && s !== 'N/A')
                                .join(' - ')}
                              checked={selectedDocumentIds.includes(document.id)}
                              onChange={(e, checked) => handleCheckDocument(checked, document.id)}
                            />
                          }
                          classes={{
                            label: clsx(classes.treeLabel, classes.treeLabelChild),
                            group: classes.treeGroup,
                            content: classes.treeContent,
                            iconContainer: classes.treeIconContainer,
                          }}
                        />
                      ))}
                    </TreeItem>
                  );
                })}
              </TreeView>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between', padding: '8px 16px 16px 16px' }}>
        {!isSubmitting ? (
          <>
            <SubmitButton
              onClick={handleAssociate}
              disabled={selectedUserIds.length === 0 || selectedDocumentIds.length === 0}
            >
              Associate Selected Users with Selected {documentNoun}
            </SubmitButton>
            <CancelButton onClick={handleClose}>Close</CancelButton>
          </>
        ) : (
          <CircularLoader fullWidth />
        )}
      </DialogActions>
    </Dialog>
  );
}
